/*eslint-disable*/
import "formdata-polyfill";
import assign from "lodash/assign";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import size from "lodash/size";
import uniqueId from "lodash/uniqueId";
import request from "superagent";

import chatNavigation from "../utils/chat/chatNavigation";
import adjustMomentTime from "../utils/adjustMomentTime";
import alt from "../alt/ChatAlt";
import Analytics from "../utils/chat/Analytics";
import { EVENT_ACTIONS } from "../constants/AnalyticsConstants";
import { PostMessage } from "../constants/chat";
import { isValidUuid, postMessageToParent, validateEmail, validatePhone } from "../utils/Utils";
import ChatWebAPI from "../utils/chat/ChatWebAPI";
import JwtHelper from "../utils/chat/JwtHelper";
import { triggerAskQuestionEvent, triggerOnErrorEvent } from "../utils/chat/EventHelper";

export class ChatActions {
    constructor() {
        this.generateActions(
            "authFail",
            "authSuccess",
            "askQuestionFail",
            "askQuestionSuccess",
            "clearActionBar",
            "createActionBar",
            "resetChat",
            "viewAnswer",
            "markMessagesAsRead",
            "cyclePotentialExperts",
            "clearPotentialExpertsTimer",
            "refreshSystemMessages",
            "resetAuth",
            "responseToggleRateToolbar",
            "responseToggleTestimonialForm",
            "showResponseSwitcher",
            "hideResponseSwitcher",
            "loadNotifications",
            "load",
            "list",
            "uploadAttachmentFail",
            "uploadAttachmentSuccess",
            "grantBrowserNotifications",
            "denyBrowserNotifications",
            "updateQuestion",
            "updateUserName",
            "updateContactInfo",
            "logoutSuccess",
            "markResponseAsReadFail",
            "loadPotentialExpertsFail",
            "loadPotentialExpertsSuccess",
            "rerouteFail",
            "rerouteSuccess",
            "closeformoptoutfail",
            "closeformoptoutSuccess",
            "responseRateFail",
            "responseRateSuccess",
            "responseAcceptFail",
            "responseAcceptSuccess",
            "responseAddTestimonialFail",
            "responseAddTestimonialSuccess",
            "addNewMessageFail",
            "addNewMessageSuccess",
            "autoResponseReplyFail",
            "autoResponseReplySuccess",
            "reviewMarkAsResolvedFail",
            "reviewMarkAsResolvedSuccess",
            "updateConfig",
            "genericError",
            "updateContactFail",
            "updateContactSuccess"
        );
    }

    auth() {
        return () =>
            ChatWebAPI.get("/chat/checkAuth")
                .cancelable(false)
                .end((error, response) => {
                    adjustMomentTime(response.header.date);
                    if (error) {
                        if (error.status === 401) {
                            this.authFail();
                        } else {
                            this.genericError({ error });
                        }
                    } else {
                        const { hasContactInfo, loggedIn } = response.body.data;

                        if (loggedIn) {
                            this.authSuccess({ hasContactInfo });
                        } else {
                            this.authFail();
                        }
                    }
                });
    }

    logout() {
        return () =>
            request.get("/logout/index").end(error => {
                if (error) {
                    console.error(error); // eslint-disable-line no-console
                } else {
                    this.logoutSuccess();
                    JwtHelper.clearJwtToken();
                    // the minimized view has its own logic for what to display; we don't need to redirect the user
                    // anywere;
                    if (!chatNavigation.isMinimizedView) {
                        chatNavigation.history.replace("/ask");
                    }
                }
            });
    }

    loadSuccess({
        data: {
            chat: { lastUpdated, question },
        },
    }) {
        ChatActions.redirectWhenNewResponseComesIn(question, chatNavigation);

        return { question, lastUpdated };
    }

    loadFail(error) {
        return { error };
    }

    loadNotificationsSuccess({ data: { openQuestions }, notifications }) {
        return { notifications, openQuestions };
    }

    loadNotificationsFail(error) {
        return { error };
    }

    listSuccess(response) {
        postMessageToParent(PostMessage.EVENT_LISTENER, {
            callbackName: "onList",
            data: {
                numberOfClosedChats: response.data.closedChats.length,
                numberOfOpenChats: response.data.openChats.length,
            },
        });

        return {
            openChatList: response.data.openChats,
            closedChatList: response.data.closedChats,
            lastUpdated: response.data.lastUpdated,
        };
    }

    listFail(error) {
        return { error };
    }

    askQuestion({
        contactInfo,
        configId,
        customTags,
        emailPrefilled,
        metadata,
        questionText,
        questionCategory,
        recaptchaValue,
        userName,
    }) {
        Analytics.sendGaEvent({ action: EVENT_ACTIONS.QuestionSubmitted });

        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/ask")
                .send({
                    cfgId: configId,
                    email: validateEmail(contactInfo) ? contactInfo : undefined,
                    name: userName,
                    text: questionText,
                    questionCategory,
                    customTags,
                    metadata,
                    emailPrefilled,
                    phone: validatePhone(contactInfo) ? contactInfo : undefined,
                    recaptchaValue,
                })
                .type("json")
                .end((error, response) => {
                    if (error) {
                        this.askQuestionFail({ error });
                        triggerOnErrorEvent({
                            cfgId: configId,
                            error,
                            userName,
                            contactInfo,
                            questionText,
                            action: "Ask_question",
                        });
                    } else {
                        const newCsrfToken = response.header["x-csrf-token"];
                        const currentCsrfTokenElement = document.querySelector("head > meta[name=_csrf]");
                        const { question } = response.body.data.chat;

                        if (
                            currentCsrfTokenElement &&
                            currentCsrfTokenElement.getAttribute("content") !== newCsrfToken
                        ) {
                            currentCsrfTokenElement.setAttribute("content", newCsrfToken);
                        }
                        chatNavigation.history.push(`question/${question.uuid}`);
                        triggerAskQuestionEvent(question);
                        this.askQuestionSuccess({ contactInfo });
                    }
                });
        };
    }

    markResponseAsRead({ questionUuid, responseId }) {
        return dispatch => {
            dispatch({ responseId });
            ChatWebAPI.post("/chat/markResponseAsViewed")
                .send({ chatId: questionUuid, responseId })
                .end(error => {
                    if (error) {
                        this.markResponseAsReadFail({ error });
                    }
                });
        };
    }

    loadPotentialExperts({ configId, questionUuid }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.get("/chat/potentialExperts")
                .query({ chatId: questionUuid, cfgId: configId })
                .end((error, response) => {
                    if (error) {
                        this.loadPotentialExpertsFail({ error });
                    } else {
                        this.loadPotentialExpertsSuccess({
                            experts: response.body.data.potentialExperts,
                            lastResponseTimeInMillis: response.body.data.lastResponseTimeInMills,
                        });
                    }
                });
        };
    }

    loadLastResponseTime(configId) {
        return dispatch => {
            dispatch();
            ChatWebAPI.get("/chat/lastResponseTime")
                .query({ cfgId: configId })
                .end((error, response) => {
                    if (error) {
                        this.loadLastResponseTimeFail(error);
                    } else if (response.body.data.lastResponseTimeInMillis) {
                        this.loadLastResponseTimeSuccess(response);
                    } else {
                        this.loadLastResponseTimeFail(error);
                    }
                });
        };
    }

    loadLastResponseTimeFail(error) {
        return { error };
    }

    loadLastResponseTimeSuccess(response) {
        return {
            lastResponseTimeInMillis: response.body.data.lastResponseTimeInMillis,
        };
    }

    reroute(questionUuid) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/reroute")
                .deferHeartbeat()
                .send({ chatId: questionUuid })
                .end((error, response) => {
                    if (error) {
                        this.rerouteFail({ error });
                    } else {
                        this.rerouteSuccess({ question: response.body.data.chat.question });
                    }
                });
        };
    }

    closeformoptout(questionUuid) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post(`/chat/closeFromOptOut`)
                .deferHeartbeat()
                .send({ chatId: questionUuid, future: true })
                .end((error, response) => {
                    if (error) {
                        this.closeformoptoutfail({ error });
                    } else {
                        this.closeformoptoutSuccess({ question: response.body.data.chat.question });
                    }
                });
        };
    }

    responseRate({ newHappiness, prevHappiness, questionUuid, responseId }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/rate")
                .deferHeartbeat()
                .send({
                    chatId: questionUuid,
                    responseId,
                    customerHappiness: newHappiness,
                })
                .end((error, response) => {
                    if (error) {
                        this.responseRateFail({ error, prevHappiness });
                    } else {
                        this.responseRateSuccess({ question: response.body.data.chat.question });
                        this.responseToggleRateToolbar();
                    }
                });
        };
    }

    responseAccept({ responseId, questionUuid, prevHappiness }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/acceptAnswer")
                .deferHeartbeat()
                .send({ chatId: questionUuid, responseId })
                .end((error, response) => {
                    if (error) {
                        this.responseAcceptFail({ error, prevHappiness });
                    } else {
                        this.responseAcceptSuccess({
                            question: response.body.data.chat.question,
                            responseId,
                        });
                    }
                });
        };
    }

    responseAddTestimonial({ expertId, questionUuid, text }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/addTestimonial")
                .deferHeartbeat()
                .send({ chatId: questionUuid, expertId, text })
                .end((error, response) => {
                    if (error) {
                        this.responseAddTestimonialFail({ error });
                    } else {
                        this.responseAddTestimonialSuccess({ question: response.body.data.chat.question });
                        this.responseToggleTestimonialForm();
                    }
                });
        };
    }

    addNewMessage({ messageText, questionUuid, responseId }) {
        const tempId = Number(uniqueId("99999"));

        return dispatch => {
            dispatch({
                id: tempId,
                messageText,
                responseId,
            });
            ChatWebAPI.post("/chat/message")
                .deferHeartbeat()
                .send({
                    chatId: questionUuid,
                    responseId,
                    text: messageText,
                })
                .end((error, response) => {
                    if (error) {
                        this.addNewMessageFail({ error });
                    } else {
                        this.addNewMessageSuccess({ message: response.body.data, tempId, questionUuid });
                    }
                });
        };
    }

    autoResponseReply({ autoResponseId, questionUuid, quickReply }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/addAutoResponseQuickReply")
                .deferHeartbeat()
                .send({
                    chatId: questionUuid,
                    autoResponseId,
                    quickReply,
                })
                .end((error, response) => {
                    if (error) {
                        this.autoResponseReplyFail({ error });
                    } else {
                        this.autoResponseReplySuccess({ question: response.body.data.chat.question });
                    }
                });
        };
    }

    reviewMarkAsResolved({ confirm, messageId, questionUuid }) {
        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/reviewMarkAsResolved")
                .query({ chatId: questionUuid, confirm, messageId })
                .end((error, response) => {
                    if (error) {
                        this.reviewMarkAsResolvedFail({ error });
                    } else {
                        this.reviewMarkAsResolvedSuccess({ confirm, question: response.body.data.chat.question });
                    }
                });
        };
    }

    uploadAttachment({ chatConfigId, file, questionUuid, responseId }) {
        const formData = new FormData();
        const tempId = uniqueId("tempAttachmentId_");

        formData.append("cfgId", chatConfigId);
        formData.append("chatId", questionUuid);
        formData.append("responseId", responseId);

        return dispatch => {
            dispatch({ tempId, responseId });
            ChatWebAPI.post("/chat/upload")
                .deferHeartbeat()
                .setFile(file)
                .send(formData)
                .end((error, response) => {
                    if (error) {
                        this.uploadAttachmentFail(error);
                    } else {
                        this.uploadAttachmentSuccess(assign({}, { tempId }, response.body.data));
                    }
                });
        };
    }

    updateContact({ chatConfigId, chatId, contact }) {
        const email = validateEmail(contact) ? contact : undefined;
        const phone = validatePhone(contact) ? contact : undefined;

        return dispatch => {
            dispatch();
            ChatWebAPI.post("/chat/updateContact")
                .deferHeartbeat()
                .send({ cfgId: chatConfigId, chatId, email, phone })
                .end((error, response) => {
                    if (error) {
                        this.updateContactFail(error);
                    } else {
                        const { question } = response.body.data.chat;

                        ChatActions.redirectWhenNewResponseComesIn(question, chatNavigation);
                        this.updateContactSuccess({ contactInfo: email || phone, question });
                    }
                });
        };
    }

    static redirectWhenNewResponseComesIn(question, navigation) {
        const shouldRedirectToFirstResponse =
            size(question.response) > 0 && isEmpty(navigation.location.query.responseId);
        const questionUuid = navigation.location.pathname.split("/")[2];

        if (!isValidUuid(questionUuid)) {
            navigation.history.replace(`question/${question.uuid}`);
        }

        if (shouldRedirectToFirstResponse) {
            navigation.history.replace(`question/${question.uuid}?responseId=${get(question, "response[0].id")}`);
        }
    }
}

export default alt.createActions(ChatActions);
